/**
 * The `app_slug` values that appear in a token's `bh_claims.products[]`. These come
 * from `user_app_entitlements.application`, written by vercel-auth-functions from its
 * own `VALID_PRODUCTS` list and validated by the identity DB's `handle_new_user`
 * trigger against the same five.
 *
 * Deliberately NOT `productHelpers.Product`, which is a routing/theming vocabulary:
 * it carries `sleepio-canopy` (a flow name that is not an app_slug) and omits the Rx
 * variants. Passing a Product here would match no entry and read every member as new.
 */
export type EntitlementApp = 'spark' | 'sleepio' | 'sleepiorx' | 'daylight' | 'daylightrx';

/**
 * Maps a route's product segment to the app_slug its entitlement rows are keyed on.
 *
 * Takes a plain string, not `productHelpers.Product`, on purpose. The value reaches us
 * from the URL — vercel-auth-functions builds the callback as
 * `{base}/{product}/{lob}/activate/email/callback` (`lib/supabaseAdmin.ts:1038`) using
 * its own five-value product list, so `sleepiorx` and `daylightrx` genuinely appear
 * there. `useParams<{ product: Product }>()` asserts the narrower four-value type
 * without validating it, so typing this parameter as `Product` would let an Rx segment
 * fall through a `default:` branch and silently resolve to the wrong product.
 *
 * Returns undefined for an unrecognised segment; callers treat that as "cannot tell",
 * which lands on the same safe default as an unreadable token.
 */
export const toEntitlementApp = (product: string): EntitlementApp | undefined => {
  switch (product) {
  case 'spark':
  case 'sleepio':
  case 'sleepiorx':
  case 'daylight':
  case 'daylightrx':
    return product;
  // Both are the Sleepio product; 'sleepio-canopy' names the flow, not an app_slug.
  case 'sleepio-canopy':
    return 'sleepio';
  default:
    return undefined;
  }
};

/**
 * Reads one product's entitlement status out of a Supabase access token's `bh_claims`.
 *
 * Shared by the Sleepio and Spark email callbacks, which both decide "is this a
 * returning user?" from it. They each carried their own copy and drifted apart once
 * already (SD-2335 / PAT-1281), so there is one implementation now.
 *
 * `app` is required. The claim lists every product the user holds, ordered
 * alphabetically by the access-token hook, so reading index 0 returned whichever
 * product happened to sort first — a Sleepio user also holding Daylight always read
 * as Daylight's status (PAT-1288).
 *
 * Returns undefined when the token can't be read, or when the user holds no
 * entitlement for `app` — both of which every caller treats as "not a returning user".
 */
export const getEntitlementStatus = (
  accessToken: string,
  app: EntitlementApp,
): string | undefined => {
  try {
    // JWT payloads are base64url, which uses `-` and `_` where standard base64 uses `+`
    // and `/`. atob() rejects those, so a token containing either would decode to
    // undefined here — reading as "not a returning user" and sending a login down the
    // sign-up branch.
    //
    // Padding does not need restoring: an unpadded base64 segment's length mod 4 is
    // always 0, 2 or 3, never 1, and WHATWG forgiving-base64 (browsers, Node, jsdom)
    // accepts all three. Only length mod 4 === 1 throws, which a well-formed JWT
    // segment cannot produce.
    const base64 = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    const products: { app_slug?: string; status?: string }[] = payload.bh_claims?.products ?? [];
    return products.find((p) => p.app_slug === app)?.status;
  } catch {
    return undefined;
  }
};
